import axios, {type AxiosInstance, type AxiosRequestConfig,} from 'axios'
// import {useRouter} from "vue-router";
import JwtService from "@/services/jwt";
import {router} from "@/router";
import LogService, {LogIslem, LogSayfa} from "@/services/log";
import {i18n} from "@/plugins/i18n";

const t = (key: string, params?: Record<string, unknown>): string => {
  return params ? i18n.global.t(key, params) as string : i18n.global.t(key) as string;
};

interface ApiResponse<T> {
  data: T
  status: number
  error_code: number
  error_message: string
}

export type ApiError = string | null
type ApiRequestResult<T = unknown> = [ApiError, ApiResponse<T> | null];

class ApiService {
  static instance: AxiosInstance | null = null;

  static _baseUrl: string = '';
  static get baseUrl(): string {
    if (ApiService._baseUrl !== '') {
      return ApiService._baseUrl;
    }
    let tenantCode: string = import.meta.env.VITE_DEFAULT_TENANT_CODE as string ?? '';
    if (tenantCode === '') {
      tenantCode = window.location.hostname.split('.')[0] ?? '';
    }
    const bu = (import.meta.env.VITE_API_URL as string) ?? ''
    ApiService._baseUrl = `${bu}/${tenantCode}/`;
    return ApiService._baseUrl;
  }
  static init(): void {
    ApiService.instance = axios.create({
      baseURL: ApiService.baseUrl,
    })
    ApiService.instance.defaults.headers.common.Accept = 'application/json'
    ApiService.instance.interceptors.request.use(
      req => {
        const token = JwtService.getAccessToken()
        if (token && req.headers)
          req.headers.Authorization = `Bearer ${token}`

        const locale = i18n.global.locale.value || 'en'
        if (req.headers)
          req.headers['Accept-Language'] = locale

        return req
      },
    )
  }

  static async request<T = unknown>(config: AxiosRequestConfig): Promise<ApiRequestResult<T>> {
    if (!ApiService.instance)
      this.init()

    if (!ApiService.instance)
      return [t('api_errors.server_config_error'), null]

    for (let i = 0; i < 3; i++) {
      let data: ApiResponse<T> | null
      try {
        const response = await ApiService.instance.request<ApiResponse<T>>(config)
        data = response.data

        return [null, data]
      } catch (error: unknown) {
        if (!axios.isAxiosError(error))
          return [t('api_errors.unexpected_error'), null]

        const resp = error.response
        if (!resp)
          return [t('api_errors.connection_error'), null]

        if (resp.status === 400) {
          const message = (resp.data?.error_message as string | undefined) ?? t('api_errors.invalid_request')
          const errorCode = (resp.data?.error_code as string | undefined) ?? ''
          if (errorCode === 'COMPETITION_ENDED') {
            router.push('/')
          }
          return [message, resp.data]
        }
        if (resp.status === 429) {
          const retryAfter = resp.headers['retry-after']
          const dakika = retryAfter ? Math.ceil(parseInt(retryAfter) / 60) : 5
          return [t('api_errors.too_many_requests', { minutes: dakika }), null]
        }
        if (resp.status === 401 && !config.url?.includes('giris')) {
          JwtService.destroyTokens()
          await router.push('/giris')
          return [t('api_errors.please_login'), null]
        }

        if (resp.status === 403) {
          const contentType = resp.headers['content-type'] || ''
          if (contentType.includes('text/html')) {
            return [t('api_errors.security_verification_required'), null]
          }
          return [t('api_errors.authorization_error'), null]
        }
        if (resp.status === 404) {
          const message = (resp.data?.error_message as string | undefined) ?? t('api_errors.not_found')
          return [message, null]
        }

        // retry after 1, 2, 3 seconds
        if (i < 2) {
          await new Promise<void>((resolve) => setTimeout(resolve, (i + 1) * 1000));
          continue
        }
        LogService.log(LogSayfa.API, LogIslem.HATA, {
          detay: "apiservice",
          data: resp?.data,
          status: resp?.status,
          url: config.url
        })
        if (resp.status === 500)
          return [t('api_errors.server_error'), null]

        const msg = (resp.data?.error_message as string | undefined) || t('api_errors.unknown_error')
        return [msg, resp.data as ApiResponse<T>]
      }
    }

    return [t('api_errors.unknown_error'), null]
  }

  static async get<T = unknown>(route: string, urlParams?: AxiosRequestConfig['params']): Promise<ApiRequestResult<T>> {
    return this.request<T>({
      method: 'get',
      url: `/${route}`,
      params: urlParams,
    })
  }

  static async post<T = unknown, D = unknown>(
    route: string,
    data?: D,
    urlParams?: AxiosRequestConfig['params'],
    headers?: AxiosRequestConfig['headers'],
  ): Promise<ApiRequestResult<T>> {
    return this.request<T>({
      method: 'post',
      url: `/${route}`,
      data,
      params: urlParams,
      headers,
    })
  }

  static put<T = unknown, D = unknown>(
    route: string,
    data?: D,
    urlParams?: AxiosRequestConfig['params'],
  ): Promise<ApiRequestResult<T>> {
    return this.request<T>({
      method: 'put',
      url: `/${route}`,
      data,
      params: urlParams,
    })
  }

  static delete<T = unknown>(route: string, urlParams?: AxiosRequestConfig['params']): Promise<ApiRequestResult<T>> {
    return this.request<T>({
      method: 'delete',
      url: `/${route}`,
      params: urlParams,
    })
  }
}

export default ApiService
export type {ApiResponse}
